import BodyText from 'LuxKit/Typography/BodyText'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import VerticalSpacer from '../Spacing/VerticalSpacer'
import { rem } from 'polished'
import { TrustPilotStars } from '../TrustIndicators/TrustPilotStars'
import IconButton from 'LuxKit/Button/IconButton'
import LineAngleLeftIcon from 'LuxKit/Icons/line/LineAngleLeftIcon'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import GeoContext from 'contexts/geoContext'

type RegionalAuthor = {
  default: string
  [key: string]: string | undefined
}

type Review = {
  id: number
  title: string
  content: string
  author: RegionalAuthor
  link: string
}

type ReviewMetadata = Omit<Review, 'title' | 'content'>

const TRUSTPILOT_URL = 'https://au.trustpilot.com/review/luxuryescapes.com'

const reviewMetadata: Array<ReviewMetadata> = [
  {
    id: 1,
    author: {
      default: 'Peter W.',
      SG: 'James Tan',
      HK: 'Sarah Yuen',
    },
    link: TRUSTPILOT_URL,
  },
  {
    id: 2,
    author: {
      default: 'Fiona W.',
      SG: 'Jessica Lui',
      HK: 'Mei Ling Cheng',
    },
    link: TRUSTPILOT_URL,
  },
  {
    id: 3,
    author: {
      default: 'Cate B.',
      SG: 'William Ong',
      HK: 'Sze Mei Ho',
    },
    link: TRUSTPILOT_URL,
  },
  {
    id: 4,
    author: {
      default: 'Juliana C.',
      SG: 'Rahul Shankar',
      HK: 'Jessica Man',
    },
    link: TRUSTPILOT_URL,
  },
  {
    id: 5,
    author: {
      default: 'Brady B.',
      SG: 'Alexander Lim',
      HK: 'David Wong',
    },
    link: TRUSTPILOT_URL,
  },
  {
    id: 6,
    author: {
      default: 'Paul P.',
      SG: 'Melissa Wong',
      HK: 'Chun Hei Chan',
    },
    link: TRUSTPILOT_URL,
  },
  {
    id: 7,
    author: {
      default: 'Jean L.',
      SG: 'Nicole Lee',
      HK: 'Olivia Chung',
    },
    link: TRUSTPILOT_URL,
  },
  {
    id: 8,
    author: {
      default: 'Karen T.',
      SG: 'Olivia Chew',
      HK: 'Yee Ling Wong',
    },
    link: TRUSTPILOT_URL,
  },
  {
    id: 9,
    author: {
      default: 'Amal H.',
      SG: 'Hannah Ang',
      HK: 'Victoria Lui',
    },
    link: TRUSTPILOT_URL,
  },
  {
    id: 10,
    author: {
      default: 'Vicky C.',
      SG: 'Marcus Koh',
      HK: 'Andrew Chan',
    },
    link: TRUSTPILOT_URL,
  },
]
const CarouselContainer = styled.div`
  width: 100%;
  max-width: ${rem(600)};
  overflow: hidden;
  position: relative;
  margin: auto;
  padding-bottom: ${rem(5)};
`

const Track = styled.div<{ $count: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $count }) => $count}, 100%);
  width: 100%;
  transition: transform 0.5s ease-in-out;
  margin-bottom: ${rem(16)};
  cursor: pointer;
`

const ReviewCard = styled.div`
  padding: 0 ${rem(6)};
  text-align: left;
`

const NavigationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${rem(12)};
  margin-top: ${rem(12)};
`

const ArrowButton = styled(IconButton)`
  width: ${rem(28)};
  height: ${rem(28)};
  transition: color 0.2s, background-color 0.2s, border-color 0.2s, opacity 0.2s;
`

const DotsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: ${rem(8)};
`

const Dot = styled.button`
  width: ${rem(8)};
  height: ${rem(8)};
  border-radius: 50%;
  background: ${({ theme }) => theme.palette.neutral.default.five};
  border: none;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin: 0;

  &.active {
    transform: scale(1.5);
    background: ${({ theme }) => theme.palette.neutral.default.one};
  }
`

const autoScrollSpeed = 5000

function TrustPilotCarousel() {
  const intl = useIntl()
  const { currentRegionCode } = useContext(GeoContext)
  const [index, setIndex] = useState(0)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const reviews = useMemo<Array<Review>>(() => [
    {
      ...reviewMetadata[0],
      title: intl.formatMessage({
        id: 'PXO3IUSn4qKQgPN49a9G',
        defaultMessage: '4th trip, great prices',
        description: 'Trustpilot review title shown in the homepage review carousel',
      }),
      content: intl.formatMessage({
        id: 'cByTSNWviWWz1qBKVLA9',
        defaultMessage: '3 years, 4th trip great prices, great service always helpful shout out to Gemma E very helpful, fast response',
        description: 'Trustpilot review content shown in the homepage review carousel',
      }),
    },
    {
      ...reviewMetadata[1],
      title: intl.formatMessage({
        id: 'tBk85SaNvKVs2NCnPtgy',
        defaultMessage: 'We have just returned from The Haven in…',
        description: 'Trustpilot review title shown in the homepage review carousel',
      }),
      content: intl.formatMessage({
        id: 'jvOXXp2LN7TaAtybApHf',
        defaultMessage: 'We have just returned from The Haven in Khao Lak in Phuket. Wendy P. was our agent from Luxury Escapes and did a fabulous job for us. The trip went very well the hotel and package was amazing. The hotel staff were all very helpful and supportive. Great holiday - thanks LE.',
        description: 'Trustpilot review content shown in the homepage review carousel',
      }),
    },
    {
      ...reviewMetadata[2],
      title: intl.formatMessage({
        id: '8VQq9Qb5VKl0skNpHByN',
        defaultMessage: 'Great service!',
        description: 'Trustpilot review title shown in the homepage review carousel',
      }),
      content: intl.formatMessage({
        id: 'p9rHaDSs9oy0i6hQln3r',
        defaultMessage: 'I have just returned from my first holiday where I was astounded by the value for money, so on my return I went straight back online to book my next break. Excellent service received from Joel S today when I needed to amend my second booking. Happy days!',
        description: 'Trustpilot review content shown in the homepage review carousel',
      }),
    },
    {
      ...reviewMetadata[3],
      title: intl.formatMessage({
        id: 'S8VDTwFp1FnDo0tTBy5E',
        defaultMessage: 'Really appreciate the wonderful service',
        description: 'Trustpilot review title shown in the homepage review carousel',
      }),
      content: intl.formatMessage({
        id: '4YWH29YXT7CMbkFyaQiG',
        defaultMessage: 'Really appreciate the wonderful service and advice from Stephanie at Luxury Escapes! I was so well looked after - and now my husband and I have a well thought out holiday to look forward to!! Thank you Steph and Luxury Escapes- we cannot wait!!!',
        description: 'Trustpilot review content shown in the homepage review carousel',
      }),
    },
    {
      ...reviewMetadata[4],
      title: intl.formatMessage({
        id: 'JDXvfRoDaU6zaaFI-aw6',
        defaultMessage: 'We love Luxury Escapes',
        description: 'Trustpilot review title shown in the homepage review carousel',
      }),
      content: intl.formatMessage({
        id: 'S1pTn0LR09jcOykiAreY',
        defaultMessage: 'Kurniawan C. was so helpful, my request was confirmed within 15 minutes and I could not be more grateful. He was very kind and professional. This is why we love Luxury Escapes',
        description: 'Trustpilot review content shown in the homepage review carousel',
      }),
    },
    {
      ...reviewMetadata[5],
      title: intl.formatMessage({
        id: '8c7YWKnLTnAEpL_Y7Gb6',
        defaultMessage: 'Gemma was so helpful with my enquire…',
        description: 'Trustpilot review title shown in the homepage review carousel',
      }),
      content: intl.formatMessage({
        id: 'Zju-cAVYyoKVdRBjcKzB',
        defaultMessage: 'Gemma was so helpful with my enquire and followed up everything, calling me back three times to make sure all was good. Could not be happier with her service.',
        description: 'Trustpilot review content shown in the homepage review carousel',
      }),
    },
    {
      ...reviewMetadata[6],
      title: intl.formatMessage({
        id: 'XuEoFv7ionzrdVpGZIfA',
        defaultMessage: 'Great customer experience',
        description: 'Trustpilot review title shown in the homepage review carousel',
      }),
      content: intl.formatMessage({
        id: 'LMeoWpFW-c1HClYD4K80',
        defaultMessage: 'Great customer experience, Emily G was so respectful, friendly and help me with my inquiries in a very good manner. Was a pleasure talking to her, well done 👏 Luxury Escapes and Emily G.',
        description: 'Trustpilot review content shown in the homepage review carousel',
      }),
    },
    {
      ...reviewMetadata[7],
      title: intl.formatMessage({
        id: 'TiQKl-UfVQlNtTtDbJis',
        defaultMessage: 'Dealing with great staff is a bonus and…',
        description: 'Trustpilot review title shown in the homepage review carousel',
      }),
      content: intl.formatMessage({
        id: 'gGRJ0UnLrlxvaBjotAwN',
        defaultMessage: 'Dealing with great staff is a bonus and Sarah B is amazing nothing was any trouble she is a pleasure to deal with 10/10. Being we had to cancel our trip Sarah was very efficient and understanding of our circumstances and being as we have book through Luxury Escapes before and have had amazing holiday’s in the past this is just a hiccup before we book our next trip.',
        description: 'Trustpilot review content shown in the homepage review carousel',
      }),
    },
    {
      ...reviewMetadata[8],
      title: intl.formatMessage({
        id: 'Or2G1jsh059nwxEUQbeF',
        defaultMessage: 'Sarah T was very helpful',
        description: 'Trustpilot review title shown in the homepage review carousel',
      }),
      content: intl.formatMessage({
        id: 'YVDk9JZmwl3X1zI8VS4h',
        defaultMessage: 'Sarah T was very helpful. After few phone call with different agents, Sarah was able to sort out my issue. She actually rang me back to finalize everything and ensure everything is sorted. Great customer service. Well done Luxury Escapes',
        description: 'Trustpilot review content shown in the homepage review carousel',
      }),
    },
    {
      ...reviewMetadata[9],
      title: intl.formatMessage({
        id: '-sQoVOmLSPJkhBmR6DcM',
        defaultMessage: 'Great deals and five star service',
        description: 'Trustpilot review title shown in the homepage review carousel',
      }),
      content: intl.formatMessage({
        id: 'klcK96eklVsypLhoyJh0',
        defaultMessage: 'Great deals and five star service. Had to make a change to my booking and Joel S was very helpful and very efficient. Will always use Luxury Escapes for all future bookings.',
        description: 'Trustpilot review content shown in the homepage review carousel',
      }),
    },
  ], [intl])

  const handleClick = (link: string) => {
    window.open(link, '_blank')
  }

  const handleDotClick = (i: number) => {
    setIndex(i)
    resetAutoScroll()
  }

  const goPrev = () => {
    setIndex((prev) => Math.max(prev - 1, 0))
    resetAutoScroll()
  }

  const goNext = () => {
    setIndex((prev) => Math.min(prev + 1, reviews.length - 1))
    resetAutoScroll()
  }
  const startAutoScroll = useCallback(() => {
    timeoutRef.current = setTimeout(() => {
      setIndex((prev) => (prev + 1) % reviews.length)
    }, autoScrollSpeed)
  }, [reviews.length])

  const resetAutoScroll = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    startAutoScroll()
  }

  useEffect(() => {
    startAutoScroll()
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [index, startAutoScroll])

  return (
    <CarouselContainer>
      <Track $count={reviews.length} style={{ transform: `translateX(-${index * 100}%)` }}>
        {reviews.map((review) => (
          <ReviewCard key={review.id} onClick={() => handleClick(review.link)}>
            <VerticalSpacer gap={12}>
              <TrustPilotStars width="76" height="14" />
              <BodyText weight="bold" variant="medium" colour="primary-dark">
                {review.title}
              </BodyText>
            </VerticalSpacer>
            <VerticalSpacer gap={12}>
              <BodyText variant="medium" lineClamp={3}>{review.content}</BodyText>
              <BodyText variant="medium" colour="neutral-three">
                {review.author[currentRegionCode] ?? review.author.default}
              </BodyText>
            </VerticalSpacer>
          </ReviewCard>
        ))}
      </Track>
      <NavigationContainer>
        <ArrowButton
          kind="secondary"
          shape="circle"
          onClick={goPrev}
          disabled={index === 0}
        ><LineAngleLeftIcon />
        </ArrowButton>
        <DotsContainer>
          {reviews.map((_, i) => (
            <Dot key={i} className={index === i ? 'active' : ''} onClick={() => handleDotClick(i)} />
          ))}
        </DotsContainer>
        <ArrowButton
          kind="secondary"
          size="small"
          shape="circle"
          onClick={goNext}
          disabled={index === reviews.length - 1}
        ><LineAngleRightIcon />
        </ArrowButton>
      </NavigationContainer>
    </CarouselContainer>
  )
}

export default TrustPilotCarousel
